import { AddIcon } from "asset/icons";
import NewDashboardLayout from "../../component/layout/NewDashboardLayout";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import { useQueryState } from "nuqs";
import { SearchInput } from "../../component/TableSearchInput";
import { DocumentDownload } from "iconsax-reactjs";
import {
    invoiceCsvHeaders,
    invoiceTabType,
    sortByFilter,
    sortByTimeFrame as filterByTimeFrame,
    statusFilterTabs,
} from "constants/global";
import FilterButton from "../../component/FilterDropdown";
import { useCallback, useMemo, useState } from "react";
import DeleteFlexInvoice from "component/DeleteFlexInvoice";
import RecordPayment from "component/flex/RecordPayment";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
    handleDeleteSingleInvoice,
    handleDownloadInvoice,
    handleGetAllInvoice,
    handleGetInvoiceSummary,
    handleGetWallets,
    handleMakePayment,
} from "api/fetchers";
import { formatMoney } from "utilities/formatMoney";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import CustomHeader from "component/CustomHeader";
import {
    incomingInvoiceDropDown,
    outGoingInvoiceDropDown,
} from "component/flex/invoice/invoiceConstants";
import InvoiceAnalytics from "component/flex/invoice/InvoiceAnalytics";
import PinModal from "component/pay/PinModal";
import MakePayModal from "component/flex/invoice/modal/MakePayModal";
import InvoiceTable from "component/flex/invoice/InvoiceTable";
import Swal from "sweetalert2";

const InvoicePage = () => {
    const queryClient = useQueryClient();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { business, currency } = useSelector((state) => state);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [recordPaymentModal, setRecordPaymentModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [makePaymentModal, setMakePaymentModal] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [amount, setAmount] = useState("");
    const [paymentType, setPaymentType] = useState("");
    const isBusinessApproved = business?.status === "APPROVED";
    const [activeTab, setActiveTab] = useQueryState("activeTab", {
        defaultValue: invoiceTabType[0],
    });
    const [activeSubTab, setActiveSubTab] = useQueryState("subTab", {
        defaultValue: statusFilterTabs[0].value,
    });
    const [sortBy, setSortBy] = useQueryState("sortBy", {
        defaultValue: sortByFilter[0].value,
    });
    const [sortByTimeFrame, setSortByTimeFrame] = useQueryState("timeFrame", {
        defaultValue: filterByTimeFrame[0].value,
    });
    const navigate = useNavigate();

    const {
        data,
        isLoading: loadingInvoice,
        refetch,
    } = useQuery({
        queryKey: [
            "alInvoice",
            business?._id,
            activeSubTab,
            activeTab,
            currentPage,
            searchQuery,
            sortBy,
            sortByTimeFrame,
        ],
        queryFn: () =>
            handleGetAllInvoice({
                businessId: business?._id,
                currency,
                type: activeTab === invoiceTabType[0] ? "outbound" : "inbound",
                statuses: activeSubTab,
                page: currentPage,
                searchTerm: searchQuery,
                sortBy: sortBy?.toLowerCase(),
                period: sortByTimeFrame,
            }),
        enabled: !!business?._id,
    });
      const { data: userWallets, refetch: refetchWallet } = useQuery({
            queryKey: ["user-wallet", currency],
            queryFn: () => handleGetWallets({ businessId: business?._id, currency }),
        });
    const { mutateAsync: makePaymentFunc, isLoading: makingPaymentLoading } = useMutation({
        mutationFn: ({ amount, pin, invoiceId }) => handleMakePayment({ invoiceId, amount: String(amount), pin }),
        mutationKey: ["make-invoice-payment"],
        onSettled: async (data) => {
            if (data?.status === 400) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: data?.response?.data?.message ||
                        data?.response?.data?.message?.[0] ||
                        data?.response?.data?.message[0] ||
                        "Something went wrong while making payment",
                });
            } else {
                toast.success(data?.message || "Payment made successfully");
                refetchWallet();
                await queryClient.invalidateQueries({
                    queryKey: ["singleInvoice"],
                });
                setShowOtp(false);
                setAmount("");
            }
        },
    });

    const { mutateAsync: deleteSingleInvoice, isLoading: deletingInvoice } = useMutation({
        mutationFn: (id) => {
            handleDeleteSingleInvoice(id);
        },
        mutationKey: ["delete-single-invoice", selectedInvoice?._id],
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: ["alInvoice", business?._id] });
            setShowDeleteModal(false);
        },
    });
    const { totalPages, inbound } = data ?? {};

    const invoiceList = useMemo(() => {
        return (
            inbound?.map(
                ({
                    title,
                    totalAmount,
                    createdAt,
                    dueDate,
                    paymentStatus,
                    businessName,
                    inbound,
                    currency,
                    outbound,
                    _id,
                    amount,
                    ...rest
                }) => ({
                    ...rest,
                    invoiceNumber: title ? title : "N/A",
                    amount: formatMoney(totalAmount),
                    totalAmount,
                    due: dueDate ? dueDate : rest?.due,
                    status: paymentStatus,
                    customer:
                        activeTab === invoiceTabType[0]
                            ? inbound?.businessName
                            : outbound?.businessName,
                    email:
                        activeTab === invoiceTabType[0] ? inbound?.email : outbound?.email || "N/A",
                    currency: currency,
                    id: _id,
                })
            ) || []
        );
    }, [data]);

    const { data: invoiceSummary, isLoading: loadingStats } = useQuery({
        queryKey: ["invoiceSummary", business?._id, currency, sortByTimeFrame],
        queryFn: () =>
            handleGetInvoiceSummary({
                businessId: business?._id,
                currency,
                period: sortByTimeFrame,
            }),
    });

    const { mutateAsync: downloadInvoiceFunc, isLoading: downloadingInvoice } = useMutation({
        mutationKey: ["duplicateInvoice", business?._id],
        mutationFn: async (invoiceId) => {
            await handleDownloadInvoice(invoiceId);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: ["alInvoice", business?._id] });
        },
        onSuccess: async () => {
            toast.success("Receipt Downloaded");
        },
        onError: async () => {
            toast.error("Failed to download receipt.");
        },
    });

    const invoiceDropDownOptions = (invoice) => {
        return activeTab === invoiceTabType[1]
            ? incomingInvoiceDropDown({
                  invoice,
                  navigate,
                  downloadInvoiceFunc,
                  setMakePaymentModal,
                  setSelectedInvoice,
                  setShowDeleteModal,
              })
            : outGoingInvoiceDropDown({
                  invoice,
                  navigate,
                  downloadInvoiceFunc,
                  setRecordPaymentModal,
                  setSelectedInvoice,
                  setShowDeleteModal,
              });
    };

    const handleSearchChange = useCallback(
        debounce((searchString) => {
            setSearchQuery(searchString.trim().toLowerCase());
        }, 700),
        []
    );

    const subHeaderToShow =
        activeTab === invoiceTabType?.[0]
            ? statusFilterTabs
            : statusFilterTabs.slice(0, statusFilterTabs?.length - 1);

    const walletBalance = userWallets?.balance;

    return (
        <NewDashboardLayout>
            <main className="">
                <CustomHeader title={"Invoices"}>
                    <Link
                        to={"/invoice/create-invoice"}
                        className={`hover:opacity-90 bg-[#6F00FF] mt-0 sm:!mt-0 flex items-center gap-2 text-white rounded-lg py-[10px] px-3 md:!px-5 text-sm`}
                    >
                        <AddIcon className=" stroke-white fill-white size-4" />
                        <span>Create invoice</span>
                    </Link>
                </CustomHeader>
                <InvoiceAnalytics
                    isBusinessApproved={isBusinessApproved}
                    business={business}
                    loadingStats={loadingStats}
                    invoiceSummary={invoiceSummary}
                    currency={currency}
                    sortByTimeFrame={sortByTimeFrame}
                    setSortByTimeFrame={setSortByTimeFrame}
                />
                <div className="my-5">
                    <div className="flex gap-x-4 md:gap-x-8 md:px-6">
                        {invoiceTabType.map((tab) => (
                            <button
                                key={tab}
                                className={`relative px-2 md:px-3 outline-0 ring-0 py-2 text-sm  transition-all duration-300 ${
                                    activeTab === tab
                                        ? "after:content-[''] font-[450] text-gray-90 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:bg-[#6F00FF] after:rounded-lg"
                                        : "text-gray-70 font-[400]"
                                }`}
                                onClick={() => {
                                    setActiveTab(tab);
                                    setActiveSubTab(subHeaderToShow[0].value);
                                }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div className="min-h-[70vh] relative rounded-xl pb-1 bg-white w-full">
                        <div className="flex w-full gap-2 p-4 mb-2 overflow-x-auto rounded-lg md:overflow-hidden md:w-7/12 sm:mb-0">
                            {subHeaderToShow.map(({ text, value: subTab }) => (
                                <button
                                    key={subTab}
                                    className={`rounded py-1 sm:py-2 px-3 text-sm font-medium transition-all whitespace-nowrap duration-300 ${
                                        subTab === activeSubTab
                                            ? "text-[#6F00FF] bg-[#EBE4FF]"
                                            : "text-main-grey"
                                    }`}
                                    onClick={() => setActiveSubTab(subTab)}
                                >
                                    {text}
                                </button>
                            ))}
                        </div>
                        <div className="flex flex-col items-center justify-between gap-2 px-4 pb-4 md:flex-row">
                            <div className="w-full md:w-3/6">
                                <SearchInput
                                    placeholder={"Search by invoice name, ID, customer etc."}
                                    onChange={(searchTerm) => {
                                        handleSearchChange(searchTerm);
                                    }}
                                />
                            </div>

                            <div className="flex flex-wrap items-center justify-center gap-2 p-2 md:gap-x-5">
                                <FilterButton
                                    type="Status"
                                    hasCaret
                                    filterList={subHeaderToShow}
                                    onSelect={(tab) => setActiveSubTab(tab)}
                                    selected={
                                        subHeaderToShow.find((item) => item.value === activeSubTab)
                                            ?.text || activeSubTab
                                    }
                                />
                                <FilterButton
                                    type="Sort"
                                    filterTitle="Sort by"
                                    hasCaret
                                    filterList={sortByFilter}
                                    onSelect={(tab) => setSortBy(tab)}
                                    selected={
                                        sortByFilter.find((item) => item.value === sortBy)?.text ||
                                        sortBy
                                    }
                                />

                                <div className="h-4" style={{ borderLeft: "1px solid #E7E9FB" }} />
                                <CSVLink
                                    filename={"invoice-list.csv"}
                                    data={invoiceList}
                                    headers={invoiceCsvHeaders}
                                >
                                    <button
                                        className="flex !gap-2 text-sm !text-secondary whitespace-nowrap items-center md:ml-auto -translate-y-[1px]"
                                        onClick={() => {}}
                                    >
                                        <DocumentDownload className="size-5" /> Generate CSV
                                    </button>
                                </CSVLink>
                            </div>
                        </div>
                        <InvoiceTable
                            loadingInvoice={loadingInvoice}
                            downloadingInvoice={downloadingInvoice}
                            invoiceList={invoiceList}
                            navigate={navigate}
                            invoiceDropDownOptions={invoiceDropDownOptions}
                            currentPage={currentPage}
                            activeTab={activeTab}
                            searchQuery={searchQuery}
                            setCurrentPage={setCurrentPage}
                            totalPages={totalPages}
                        />
                    </div>
                </div>
            </main>
            <DeleteFlexInvoice
                show={showDeleteModal}
                setshow={setShowDeleteModal}
                loader={deletingInvoice}
                deletedBuyer={() => {
                    deleteSingleInvoice(selectedInvoice?.id);
                }}
            />
            {recordPaymentModal && (
                <RecordPayment
                    order={selectedInvoice}
                    orderId={selectedInvoice?.id}
                    totalAmount={selectedInvoice?.amount}
                    close={() => setRecordPaymentModal(false)}
                    type="invoice"
                    refetchActivity={() => {
                        refetch();
                        queryClient.invalidateQueries({ queryKey: ["alInvoice", business?._id] });
                        setRecordPaymentModal(false);
                    }}
                />
            )}
            {makePaymentModal && (
                <MakePayModal
                    currency={currency}
                    walletBalance={walletBalance}
                    onClick={() => {
                        if (selectedInvoice?.amount > walletBalance) {
                            return toast.error("Amount can not be greater than wallet balance");
                        } else {
                            setMakePaymentModal(false);
                            setShowOtp(true);
                        }
                    }}
                    paymentType={paymentType}
                    showFullAmountToggle={false}
                    setPaymentType={setPaymentType}
                    maxAmount={selectedInvoice?.totalAmount - selectedInvoice?.paidAmount}
                    isLoading={makingPaymentLoading}
                    show={makePaymentModal}
                    setShow={setMakePaymentModal}
                    isRepayment={true}
                    amount={amount}
                    setAmount={setAmount}
                />
            )}
            <PinModal
                isInvoice
                isOpen={showOtp}
                goBack={() => {
                    setMakePaymentModal(true);
                    setShowOtp(false);
                }}
                onClose={() => {
                    setShowOtp(false);
                }}
                onConfirm={(data) => {
                    makePaymentFunc({ amount, pin: data, invoiceId: selectedInvoice?.id });
                }}
            />
        </NewDashboardLayout>
    );
};

export default InvoicePage;
